import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import {
  entryReworkResponseSchema,
  reworkEventsResponseSchema,
  type EntryRework,
  type ReworkEventsPage,
} from './types';

// Consultas del libro de reprocesos (spec 2026-10-14 §5): las devoluciones de un documento
// (ficha) y las de una persona en un rango (Estudio de tiempos). La empresa activa entra
// al queryKey: nunca se sirve la caché de otra empresa.

const PAGE_SIZE = 25;

interface Scope {
  companyId: string | null;
  enabled: boolean;
}

export function useEntryRework(
  entryId: string,
  { companyId, enabled }: Scope,
): UseQueryResult<EntryRework> {
  const api = useApi();
  return useQuery({
    queryKey: ['entry-rework', companyId, entryId],
    queryFn: async () =>
      entryReworkResponseSchema.parse(
        await api.get<unknown>(`/api/v1/quality/documents/${encodeURIComponent(entryId)}/rework`),
      ).data,
    // Reintentos: la política global (query-client) no repite un 404 (documento ajeno o
    // inexistente) ni un contrato inválido; un fallo de red o del servidor sí.
    enabled,
  });
}

/** Rango y filtros de la tarjeta que abre el diálogo: el detalle cuadra con su conteo. */
export interface PersonReworkFilter {
  from: string;
  to: string;
  documentTypeId: string | null;
  processAreaId: string | null;
}

/** Devoluciones atribuidas a una persona (`none` = sin atribuir) con los filtros dados. */
export function personEventsSearch(
  originator: string,
  filter: PersonReworkFilter,
  cursor: string | null,
): string {
  const qs = new URLSearchParams({ originator, from: filter.from, to: filter.to });
  if (filter.documentTypeId !== null) {
    qs.set('documentTypeId', filter.documentTypeId);
  }
  if (filter.processAreaId !== null) {
    qs.set('processAreaId', filter.processAreaId);
  }
  qs.set('limit', String(PAGE_SIZE));
  if (cursor) {
    qs.set('cursor', cursor);
  }
  return qs.toString();
}

export function usePersonRework(
  originator: string | null,
  filter: PersonReworkFilter,
  { companyId, enabled }: Scope,
): UseInfiniteQueryResult<InfiniteData<ReworkEventsPage>> {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [
      'person-rework',
      companyId,
      originator,
      filter.from,
      filter.to,
      filter.documentTypeId,
      filter.processAreaId,
    ],
    queryFn: async ({ pageParam }) =>
      reworkEventsResponseSchema.parse(
        await api.get<unknown>(
          `/api/v1/quality/rework/events?${personEventsSearch(originator ?? 'none', filter, pageParam)}`,
        ),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.meta.next_cursor,
    enabled: enabled && originator !== null,
  });
}
